import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Field, inputCls, textareaCls } from "@/components/common";
import { useMarkDirty } from "@/components/AppShell";

const EMPTY = { name: "", phone: "", email: "", service_address: "", billing_address: "", notes: "" };
export const CUSTOMER_DRAFT_KEY = "neec_draft_customer";
const readDraft = () => { try { return JSON.parse(sessionStorage.getItem(CUSTOMER_DRAFT_KEY) || "null"); } catch { return null; } };

export function CustomerDialog({ open, onClose, customer = null, onSaved, onSelectExisting, checkDuplicates = false }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  useMarkDirty("customer-dialog", open && !!form.name);
  const [same, setSame] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState(null);
  const clearDraft = () => sessionStorage.removeItem(CUSTOMER_DRAFT_KEY);
  useEffect(() => {
    if (!open) return;
    const draft = !customer ? readDraft() : null;
    setForm(customer ? { ...EMPTY, ...customer } : draft?.form ? { ...EMPTY, ...draft.form } : EMPTY);
    setSame(customer ? (!customer.billing_address || customer.billing_address === customer.service_address) : draft ? draft.same !== false : true);
    setDupes(null);
    if (draft?.form?.name) toast.message("We restored your unsaved customer details.", { id: "customer-draft-restored" });
  }, [open, customer]);
  useEffect(() => {
    // Temporary draft so an expired session never loses a half-typed customer. Cleared on save/cancel; sessionStorage dies with the tab.
    if (!open || customer) return;
    if (Object.values(form).some((v) => v)) sessionStorage.setItem(CUSTOMER_DRAFT_KEY, JSON.stringify({ form, same, saved_at: Date.now() }));
  }, [form, same, open, customer]);
  const cancel = () => { clearDraft(); onClose(); };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const create = async () => {
    setSaving(true);
    try {
      const body = { ...form, billing_address: same ? form.service_address : form.billing_address };
      const { data } = customer ? await api.put(`/customers/${customer.id}`, body) : await api.post("/customers", body);
      toast.success(customer ? "Customer updated" : "Customer added");
      clearDraft();
      qc.invalidateQueries();
      onSaved?.(data);
      onClose();
    } catch (err) { if (!err?.sessionExpired) toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!customer && checkDuplicates && dupes === null) {
      setSaving(true);
      try {
        const { data } = await api.get("/customers/duplicates", { params: { name: form.name, phone: form.phone, email: form.email, address: form.service_address } });
        if (data.length) { setDupes(data); setSaving(false); return; }
      } catch { /* advisory only */ }
      setSaving(false);
    }
    await create();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="customer-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{customer ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
        {dupes?.length ? (
          <div className="space-y-4" data-testid="duplicate-warning">
            <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-3 text-sm text-amber-900"><AlertTriangle size={18} className="shrink-0 text-amber-600" /><div><div className="font-semibold">Possible existing customer found</div><div className="text-xs mt-0.5">Pick the existing customer to avoid a duplicate, or continue creating a new one.</div></div></div>
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
              {dupes.map((c) => (
                <div key={c.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`duplicate-option-${c.id}`}>
                  <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{c.name}</div><div className="text-xs text-slate-500 truncate">{[c.phone, c.email, c.service_address].filter(Boolean).join(" · ")}</div><div className="text-[11px] text-amber-700 font-semibold">{c.match_reasons.join(", ")}</div></div>
                  <Button type="button" size="sm" className="btn-navy" onClick={() => { onSelectExisting?.(c); onClose(); }} data-testid={`duplicate-select-${c.id}`}>Select Existing</Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setDupes(null)} data-testid="duplicate-back-button">Back to form</Button>
              <Button type="button" disabled={saving} className="flex-1 h-11 btn-orange" onClick={create} data-testid="duplicate-continue-button">{saving ? "Saving..." : "Continue Creating New"}</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Name"><input data-testid="customer-name-input" className={inputCls} value={form.name} onChange={set("name")} required placeholder="Full name" autoComplete="off" /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Phone"><input data-testid="customer-phone-input" type="tel" inputMode="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(512) 555-0100" /></Field>
              <Field label="Email"><input data-testid="customer-email-input" type="email" inputMode="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="name@email.com" /></Field>
            </div>
            <Field label="Service address"><input data-testid="customer-service-address-input" className={inputCls} value={form.service_address} onChange={set("service_address")} placeholder="Street, City, State ZIP" /></Field>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={same} onChange={(e) => setSame(e.target.checked)} data-testid="customer-same-billing-checkbox" className="accent-orange-500 w-4 h-4" /> Billing address same as service address</label>
            {!same && <Field label="Billing address"><input data-testid="customer-billing-address-input" className={inputCls} value={form.billing_address} onChange={set("billing_address")} /></Field>}
            <Field label="Notes"><textarea data-testid="customer-notes-input" className={textareaCls} value={form.notes} onChange={set("notes")} placeholder="Gate codes, pets, preferences..." /></Field>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1 h-11" onClick={cancel} data-testid="customer-cancel-button">Cancel</Button>
              <Button type="submit" disabled={saving} className="flex-1 h-11 btn-orange" data-testid="customer-save-button">{saving ? "Saving..." : customer ? "Save Customer" : "Save & Use Customer"}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
